/**
 * Aufrufe des Anmeldemoduls.
 *
 * Die Typen stammen aus dem erzeugten Schema. Aendert sich die Schnittstelle,
 * brechen diese Zeilen beim Build -- und nicht erst beim Anwender.
 */

import type { components } from '../../api/schema';
import { applyTokenResponse, request, type TokenResponse } from '../../lib/apiClient';
import { clearSession } from '../../lib/session';

export type SessionInfo = components['schemas']['SessionInfo'];
export type TenantSummary = components['schemas']['TenantSummary'];
export type MfaSetup = components['schemas']['MfaSetupResponse'];
export type NotificationPreference = components['schemas']['NotificationPreferenceRead'];
export type AbsenceUpdate = components['schemas']['AbsenceUpdate'];
export type PasswordChanged = components['schemas']['PasswordChanged'];

export async function signIn(
  email: string,
  password: string,
  totpCode?: string,
): Promise<TokenResponse> {
  // Der Anmeldeendpunkt folgt dem OAuth2 Password Flow und erwartet deshalb
  // `username`/`password` form-kodiert, nicht JSON. Der Code aus der
  // Authenticator-App reist als weiteres Formularfeld mit -- F5-16.
  const tokens = await request<TokenResponse>('/auth/login', {
    method: 'POST',
    form: {
      username: email,
      password,
      grant_type: 'password',
      ...(totpCode ? { totp_code: totpCode } : {}),
    },
  });
  applyTokenResponse(tokens);
  return tokens;
}

/**
 * Erzeugt ein neues TOTP-Geheimnis -- F5-16.
 *
 * Die Antwort enthaelt das Geheimnis im Klartext; sie wird deshalb nicht
 * zwischengespeichert. Das ist das einzige Mal, dass es den Server verlaesst.
 */
export function beginMfaSetup(password: string): Promise<MfaSetup> {
  return request<MfaSetup>('/auth/mfa/setup', { method: 'POST', body: { password } });
}

export function activateMfa(code: string): Promise<undefined> {
  return request<undefined>('/auth/mfa/activate', { method: 'POST', body: { code } });
}

/**
 * Setzt den zweiten Faktor eines **anderen** Benutzers zurueck -- F5-16.
 *
 * Der Rueckweg bei verlorenem Telefon. Er ersetzt die
 * Wiederherstellungscodes, die es hier bewusst nicht gibt: Ein Code auf einem
 * Zettel ist ein zweiter Faktor, der nicht am Telefon haengt -- und damit
 * genau das, was der zweite Faktor verhindern soll. Stattdessen setzt ein
 * anderer Administrator zurueck, und der Vorgang steht im Protokoll.
 */
/**
 * Wechselt das eigene Kennwort -- docs/04, Abschnitt 4.
 *
 * Danach enden **alle** Sitzungen, auch die eigene: Wer sein Kennwort
 * wechselt, tut es haeufig, weil er einen fremden Zugriff vermutet, und ein
 * Wechsel, der die fremde Sitzung weiterlaufen liesse, waere folgenlos.
 */
export function changePassword(payload: {
  current_password: string;
  new_password: string;
}): Promise<PasswordChanged> {
  return request<PasswordChanged>('/auth/password', { method: 'POST', body: payload });
}

export function resetMfaFor(userId: string): Promise<undefined> {
  return request<undefined>(`/auth/mfa/reset/${userId}`, { method: 'POST' });
}

export function disableMfa(password: string, code: string): Promise<undefined> {
  return request<undefined>('/auth/mfa/disable', { method: 'POST', body: { password, code } });
}

export async function signOut(): Promise<void> {
  try {
    // 204 ohne Rumpf. `undefined` statt `void`: `void` ist als
    // Typargument nicht zulaessig und meint hier ohnehin
    // "es kommt nichts zurueck".
    await request<undefined>('/auth/logout', { method: 'POST' });
  } finally {
    // Auch wenn der Server nicht antwortet, ist die Sitzung hier zu Ende.
    // Ein Anwender, der auf "Abmelden" geklickt hat, soll nicht angemeldet
    // bleiben, weil das Netz gerade klemmt.
    clearSession();
  }
}

export function fetchSession(): Promise<SessionInfo> {
  return request<SessionInfo>('/auth/me');
}

export async function switchTenant(tenantId: string): Promise<TokenResponse> {
  const tokens = await request<TokenResponse>('/auth/tenant', {
    method: 'POST',
    body: { tenant_id: tenantId },
  });
  applyTokenResponse(tokens);
  return tokens;
}

/**
 * Die eigenen Benachrichtigungen -- F3-09.
 *
 * Der Server liefert die **vollstaendige** Liste der Ereignisse mit dem
 * geltenden Stand, nicht nur die gespeicherten Zeilen: Was nie eingestellt
 * wurde, gilt als eingeschaltet.
 */
export function fetchNotificationPreferences(): Promise<NotificationPreference[]> {
  return request<NotificationPreference[]>('/notification-preferences');
}

export function setNotificationPreference(
  event: string,
  byEmail: boolean,
): Promise<NotificationPreference> {
  return request<NotificationPreference>('/notification-preferences', {
    method: 'PUT',
    body: { event, by_email: byEmail },
  });
}

/** Abwesenheit und Vertretung -- F1-29. Jeder traegt seine eigene ein. */
export function setAbsence(payload: AbsenceUpdate): Promise<unknown> {
  return request<unknown>('/users/me/absence', { method: 'PUT', body: payload });
}
